/**
 * Form state and handlers for the invoice detail page.
 * Logic split across invoiceDetailForm* modules and useInvoiceDetailFormReorder.
 */
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import type { ZoomImageHandle } from '../../components/ZoomImage';
import type { InvoiceDetailResponse, InvoiceType, NexDetailLine } from '../../types/invoice';
import type { UpdateInvoiceInput } from './useUpdateInvoice';
import { useUpdateInvoice } from './useUpdateInvoice';
import {
    subscribeInvoiceUpdateFinished,
    trackInvoiceUpdate,
    useInvoiceUpdateJobPending,
    type InvoiceUpdateJobKind,
} from './invoiceUpdateJobTracker';
import { renderLineItemNumericCell as renderLineItemNumericCellFn, renderFreightItemNumericCell as renderFreightItemNumericCellFn, renderFuelItemNumericCell as renderFuelItemNumericCellFn, renderRestockingFeeItemNumericCell as renderRestockingFeeItemNumericCellFn, renderIgnoredPartNumericCell as renderIgnoredPartNumericCellFn } from './invoiceDetailRenderCells';
import {
    computeLineItemBackendIndexByCurrentIndex,
    computeLineItemOriginalByIndex,
    remapCurrentIndexAfterDelete,
} from './invoiceDetailFormMappings';
import { filterOutIgnoredTabCalculationMismatches } from '../../utils/invoiceIgnoredTabFlags';
import { omitReturnSlipAmountFlags } from '../../utils/invoiceFlagFamilyPolicy';
import { computeNexSubmitApDistPreview, type ManualPoLineInfo } from '../../utils/nexSubmitManualPo';
import { isCreditCardMiscFeeLine, sumCreditCardMiscFeeLineTotals } from '../../utils/invoicePaySettlement';
import {
    computeAboveLineItemsSubtotalFromInvoice,
    computeLocationDefaultTax,
    LOCATION_DEFAULT_TAX_COMPARE_TOLERANCE,
    locationDefaultTaxForGrandTotal,
    resolveLocationDefaultTaxRate,
} from '../../utils/locationDefaultTax';
import { isCanadianLocation } from '../../utils/isCanadianLocation';
import { resolveNexApHeaderTaxFields, shouldEmitSalesTaxAsApDistLine } from '../../utils/nexTaxFields';
import { isSalesTaxLineItemPart, materializeSalesTaxAsLineItem } from '../../utils/salesTaxAsLineItem';
import { fmtUsd } from './detailForm/invoiceDetailFormFormatters';
import type { RhsTab, LineItemField, UseInvoiceDetailFormStateOptions } from './detailForm/invoiceDetailFormTypes';
import {
    isReturnInvoiceType,
    normalizeItemsUnitPriceFromAmountAndQty,
    normalizeReturnSignedNumber,
} from './detailForm/invoiceDetailFormPartMath';
import { buildPartIndexRemapLayout, remapStoredPartIndex } from './detailForm/invoiceDetailFormPartIndexRemap';
import { computeLocalLineArithmeticIssues } from './detailForm/invoiceDetailFormLocalArithmetic';
import {
    buildCurrentReconciliationIssuesForSave as buildReconciliationIssuesPayload,
    getInvoiceTypeChangeFallbackPayload as buildInvoiceTypeChangeFallbackPayload,
    getInvoiceTypeChangeResetPayload as buildInvoiceTypeChangeResetPayload,
    getResetPayloadFromOriginal,
    lineItemEditableFields,
    lineItemFieldLabelMap,
} from './detailForm/invoiceDetailFormReconciliationPersist';
import { createRenderSummaryNumericField } from './detailForm/invoiceDetailFormSummaryNumericField';
import { useInvoiceDetailFormReorder } from './detailForm/useInvoiceDetailFormReorder';
import { useInvoiceDetailFormPartTabs } from './detailForm/useInvoiceDetailFormPartTabs';
import { buildManualMatchesFromOverrides, buildManualPoHeaderOverridesFromSelections } from './detailForm/invoiceDetailFormManualMatches';
import { computeLocalOrderTotalMismatchFlags } from './detailForm/invoiceDetailFormOrderTotalFlags';
import { buildNexSubmitComputeArgs } from './detailForm/invoiceDetailFormNexSubmitArgs';
import { evaluateNexLineMatchSubmitForUi } from '../../utils/nexLineMatchSubmitGuard';
import { allowsManualUnmatchedNexSubmit } from '../../utils/unmatchedLineItemsNexSubmitMode';

interface InvoiceSubmitPayloadPreview {
    apHeader: {
        invoiceNo: string;
        invoiceDate: string;
        invoiceAmount: number;
        freight: number;
        discount: number;
        tax: number;
        gstHSTTax: number;
        pstTax: number;
        total: number;
        invoiceType: 'REG' | 'CM';
    };
    apDist: Array<{
        globalIndex: number;
        folderNo: number;
        poHeaderID: number | null;
        poLineNo: number | null;
        description: string;
        unitList: number;
        unitCost: number;
        units: number;
        totalCost: number;
        /** `0` = matched, `1` = manual/unmatched, `2` = reserved. */
        manualLineInd: 0 | 1 | 2;
    }>;
    submitBlocked?: boolean;
    manualPoLineInfos?: ManualPoLineInfo[];
    unmatchedManualLineCount?: number;
}

export function useInvoiceDetailFormState(
    id: string | undefined,
    invoice: InvoiceDetailResponse | undefined,
    options: UseInvoiceDetailFormStateOptions
) {
    const {
        getReconciliation,
        queryClient,
        trackView,
        locationDefaultTaxRates,
        shopLocations = [],
        unmatchedLineItemsNexSubmitMode = 'dont_allow',
        nexReconciliationIdentifierMode = 'ro',
        salesTaxAsLineItem = false,
        onTeachableAmountCommitted,
    } = options;
    const toast = useToast();

    const [editedFields, setEditedFields] = useState<Partial<InvoiceDetailResponse>>({});
    const [isEditingInvoiceNumber, setIsEditingInvoiceNumber] = useState(false);
    const invoiceInputRef = useRef<HTMLInputElement>(null);
    const [editingNumericField, setEditingNumericField] = useState<string | null>(null);
    const [editingNumericValues, setEditingNumericValues] = useState<Record<string, string>>({});
    const summaryEditBaselineRef = useRef<Record<string, number>>({});
    const [taxManualOverride, setTaxManualOverride] = useState(false);
    const taxOverrideInitedRef = useRef<string | null>(null);
    const taxUserEditedRef = useRef(false);
    const taxAutoContextRef = useRef<string>('');
    const [revertModal, setRevertModal] = useState<{ fieldKey: string; fieldLabel: string } | null>(null);
    const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
    const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
    /** Global apDist line index -> NEX poHeaderID for manual orphan rows (submit-only; not saved on invoice). */
    const [manualPoHeaderSelections, setManualPoHeaderSelections] = useState<Record<number, number>>({});

    useEffect(() => {
        setManualPoHeaderSelections({});
    }, [id]);
    useEffect(() => {
        taxUserEditedRef.current = false;
        if (!id) {
            taxOverrideInitedRef.current = null;
            taxAutoContextRef.current = '';
            setTaxManualOverride(false);
        }
    }, [id]);
    const [suggestionModal, setSuggestionModal] = useState<{ partIndex: number; suggestedPartNumber: string } | null>(null);
    const [vendorSuggestionModal, setVendorSuggestionModal] = useState<{ suggestedVendorName: string } | null>(null);
    const [currentImagePage, setCurrentImagePage] = useState(1);
    const [activeRhsTab, setActiveRhsTab] = useState<RhsTab>('main');
    const [lineItemsTabIndex, setLineItemsTabIndex] = useState(0);
    const lastInitialTabInvoiceIdRef = useRef<string | null>(null);
    const zoomImageRef = useRef<ZoomImageHandle | null>(null);
    const savedFieldsRef = useRef<Partial<InvoiceDetailResponse>>({});
    const isAutoSaveRef = useRef(false);
    const isMutatingRef = useRef(false);
    const initialInvoiceTypeRef = useRef<{ invoiceId: string; value: string } | null>(null);
    const hasTrackedView = useRef(false);
    const normalizedUnitPriceInvoiceIdRef = useRef<string | null>(null);
    const preservedReconciliationRef = useRef<{
        matchedNexLines: Array<{ partIndex: number; nexLine: NexDetailLine }>;
        reconciliationMatchNotes: Array<{ partIndex: number; invoicePartNumber: string; nexPartNumber: string; reason: string }>;
    }>({ matchedNexLines: [], reconciliationMatchNotes: [] });

    const syncReconciliationFromFreshInvoice = useCallback(
        (freshInvoice: InvoiceDetailResponse | undefined) => {
            const recon = getReconciliation();
            if (!recon || !freshInvoice) return;
            const backendMatched = (freshInvoice.reconciliationIssues?.matchedNexLines ?? []).map(
                (m: { partIndex: number }) => m.partIndex,
            );
            const backendMatchedSet = new Set(backendMatched);
            const prevOverrides = recon.localMatchedNexOverrides ?? {};
            const nextOverrides: Record<number, NexDetailLine> = {};
            for (const [idxStr, nexLine] of Object.entries(prevOverrides)) {
                const idx = parseInt(idxStr, 10);
                if (!Number.isNaN(idx) && !backendMatchedSet.has(idx)) nextOverrides[idx] = nexLine;
            }
            recon.setLocalMatchedNexOverrides(nextOverrides);
            const prevFromBackend = Object.fromEntries(
                (freshInvoice.reconciliationIssues?.matchedNexLines ?? []).map((m) => [m.partIndex, m.nexLine]),
            ) as Record<number, NexDetailLine>;
            recon.setPreviouslyMatchedNexLines(prevFromBackend);
            recon.setUnmatchedPartIndices(new Set(freshInvoice.unmatchedPartIndices ?? []));
            if ((freshInvoice.reconciliationIssues?.matchedNexLines?.length ?? 0) > 0) {
                preservedReconciliationRef.current.matchedNexLines =
                    freshInvoice.reconciliationIssues!.matchedNexLines!;
            }
            const notes = (
                freshInvoice.reconciliationIssues as
                    | {
                          reconciliationMatchNotes?: Array<{
                              partIndex: number;
                              invoicePartNumber: string;
                              nexPartNumber: string;
                              reason: string;
                          }>;
                      }
                    | undefined
            )?.reconciliationMatchNotes;
            if (notes?.length) {
                preservedReconciliationRef.current.reconciliationMatchNotes = notes;
            }
        },
        [getReconciliation],
    );

    const acknowledgeSavedInvoice = useCallback((
        variables: UpdateInvoiceInput | undefined,
        freshInvoice: InvoiceDetailResponse | undefined,
        nexRefreshWarning?: { title?: string; detail?: string },
        opts?: { notify?: boolean; savedFields?: Partial<InvoiceDetailResponse> },
    ) => {
        queryClient.invalidateQueries({ queryKey: ['/invoice/activity-logs', id] });
        const savedByThisUpdate = opts?.savedFields;
        setEditedFields((prev) => {
            const saved = savedByThisUpdate ?? savedFieldsRef.current;
            const remaining = Object.fromEntries(
                Object.entries(prev).filter(
                    ([k, v]) =>
                        JSON.stringify(saved[k as keyof InvoiceDetailResponse]) !== JSON.stringify(v),
                ),
            ) as Partial<InvoiceDetailResponse>;
            return remaining;
        });
        if (
            !savedByThisUpdate ||
            JSON.stringify(savedFieldsRef.current) === JSON.stringify(savedByThisUpdate)
        ) {
            savedFieldsRef.current = {};
        }
        isMutatingRef.current = false;
        syncReconciliationFromFreshInvoice(freshInvoice);
        if (isAutoSaveRef.current || opts?.notify === false) return;
        if (nexRefreshWarning && variables?.refreshNexsyisData) {
            toast({
                title: nexRefreshWarning.title ?? 'Nexsyis refresh incomplete',
                description:
                    nexRefreshWarning.detail ??
                    'Could not reload all purchase-order data from Nexsyis. Existing matches were kept.',
                status: 'warning',
                duration: 6000,
                isClosable: true,
            });
        } else if (variables?.markAsManuallySubmitted) {
            const submitted =
                freshInvoice?.status === 'manually_submitted' || freshInvoice?.status === 'submitted';
            toast({
                title: submitted ? 'Invoice submitted' : 'Submit did not complete',
                description: submitted
                    ? 'The invoice has been marked as manually submitted.'
                    : 'The invoice is still flagged. Resolve remaining issues or check for a NEX error, then try again.',
                status: submitted ? 'success' : 'warning',
                duration: submitted ? 3000 : 6000,
                isClosable: true,
            });
        } else {
            toast({
                title: variables?.refreshNexsyisData ? 'Nexsyis data refreshed' : 'Invoice updated',
                description: variables?.refreshNexsyisData
                    ? 'Purchase-order data was reloaded from NEX and reconciliation was updated.'
                    : 'Your changes have been saved successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        }
    }, [id, queryClient, syncReconciliationFromFreshInvoice, toast]);

    const invoiceUpdateInProgress = useInvoiceUpdateJobPending(id);

    const updateKind = (variables: UpdateInvoiceInput | undefined): InvoiceUpdateJobKind => {
        if (variables?.markAsManuallySubmitted) return 'submit';
        if (variables?.refreshNexsyisData) return 'refresh';
        return 'nex_update';
    };

    const updateInvoiceMutation = useUpdateInvoice({
        onSuccess: async (data, variables) => {
            const payload = data?.data as {
                status?: string;
                jobId?: string;
                kind?: InvoiceUpdateJobKind;
            } | undefined;
            if (payload?.status === 'processing' && payload.jobId && variables) {
                isMutatingRef.current = false;
                trackInvoiceUpdate({
                    mode: 'job',
                    jobId: payload.jobId,
                    invoiceId: variables.invoiceId,
                    kind: payload.kind ?? updateKind(variables),
                    invoiceNumber: invoice?.invoiceNumber ? String(invoice.invoiceNumber) : undefined,
                    input: variables,
                    savedFields: { ...savedFieldsRef.current },
                });
                return;
            }
            try {
                await queryClient.refetchQueries({ queryKey: ['/invoice/detail', id] });
            } catch {}
            const freshInvoice = queryClient.getQueryData<InvoiceDetailResponse>(['/invoice/detail', id]);
            const nexRefreshWarning = data?.data?.nexRefreshWarning as
                | { title?: string; detail?: string; httpStatus?: number }
                | undefined;
            acknowledgeSavedInvoice(variables, freshInvoice, nexRefreshWarning);
        },
        onError: async (error: any, variables) => {
            const httpStatus = Number(error?.response?.status);
            const gatewayTimedOut = httpStatus === 503 || httpStatus === 504;
            if (gatewayTimedOut && id && variables) {
                isMutatingRef.current = false;
                trackInvoiceUpdate({
                    mode: 'completion',
                    invoiceId: id,
                    kind: updateKind(variables),
                    invoiceNumber: invoice?.invoiceNumber ? String(invoice.invoiceNumber) : undefined,
                    input: variables,
                    savedFields: { ...savedFieldsRef.current },
                    completedAtBefore: queryClient.getQueryData<InvoiceDetailResponse>(['/invoice/detail', id])?.updateCompletedAt,
                });
                return;
            }
            if (id) {
                try {
                    await queryClient.refetchQueries({ queryKey: ['/invoice/detail', id] });
                } catch {}
            }
            isMutatingRef.current = false;
            const freshInvoice = queryClient.getQueryData<InvoiceDetailResponse>(['/invoice/detail', id]);
            const hasFreshNexData =
                (freshInvoice?.reconciliationIssues?.matchedNexLines?.length ?? 0) > 0 ||
                (Array.isArray(freshInvoice?.nexResponse) && freshInvoice!.nexResponse!.length > 0);
            if (variables?.refreshNexsyisData && hasFreshNexData) {
                syncReconciliationFromFreshInvoice(freshInvoice);
                setEditedFields((prev) => {
                    const saved = savedFieldsRef.current;
                    const remaining = Object.fromEntries(
                        Object.entries(prev).filter(
                            ([k, v]) =>
                                JSON.stringify(saved[k as keyof InvoiceDetailResponse]) !== JSON.stringify(v),
                        ),
                    ) as Partial<InvoiceDetailResponse>;
                    return remaining;
                });
                savedFieldsRef.current = {};
                toast({
                    title: 'Nexsyis data refreshed',
                    description:
                        'The request timed out or returned an error, but updated matches were loaded from the server.',
                    status: 'warning',
                    duration: 6000,
                    isClosable: true,
                });
                return;
            }
            let detail = 'There was an error saving your changes. Please try again.';
            try {
                const problem = error?.response?.data?.nexError ?? error?.response?.data;
                if (problem && typeof problem === 'object') {
                    const status = problem.status ?? error?.response?.status;
                    const defaultTitle = variables?.refreshNexsyisData
                        ? 'Nexsyis data refresh failed'
                        : 'NEX invoice submission failed';
                    const title = problem.title ?? defaultTitle;
                    const msg = problem.detail ?? problem.message;
                    detail = `${title}${status ? ` (${status})` : ''}: ${msg ?? 'Please review the invoice and try again.'}`;
                }
            } catch {
                // fall back to default detail
            }
            toast({
                title: 'Failed to update invoice',
                description: detail,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });

    useEffect(() => {
        return subscribeInvoiceUpdateFinished((result) => {
            if (!id || result.invoiceId !== id || result.status !== 'succeeded') return;
            const freshInvoice = queryClient.getQueryData<InvoiceDetailResponse>(['/invoice/detail', id]);
            acknowledgeSavedInvoice(result.input, freshInvoice, result.nexRefreshWarning, {
                notify: false,
                savedFields: result.savedFields,
            });
        });
    }, [acknowledgeSavedInvoice, id, queryClient]);

    useEffect(() => {
        if (invoice && id && !hasTrackedView.current) {
            trackView({
                type: 'invoice',
                key: `invoice:${id}`,
                label: invoice.invoiceNumber ? `Invoice #${invoice.invoiceNumber}` : `Invoice • ${id}`,
                path: `/invoices/${id}`,
                invoiceId: id,
                meta: { invoiceNumber: invoice.invoiceNumber },
            });
            hasTrackedView.current = true;
        }
    }, [invoice, id, trackView]);

    useEffect(() => {
        if (!invoice?.reconciliationIssues) return;
        const issues = invoice.reconciliationIssues;
        if ((issues.matchedNexLines?.length ?? 0) > 0) {
            preservedReconciliationRef.current.matchedNexLines = issues.matchedNexLines!;
        }
        const notes = (issues as { reconciliationMatchNotes?: Array<{ partIndex: number; invoicePartNumber: string; nexPartNumber: string; reason: string }> }).reconciliationMatchNotes;
        if (notes?.length) {
            preservedReconciliationRef.current.reconciliationMatchNotes = notes;
        }
    }, [invoice?.reconciliationIssues]);

    useEffect(() => {
        if (!id) return;
        setLineItemsTabIndex(0);
        lastInitialTabInvoiceIdRef.current = null;
    }, [id]);

    useEffect(() => {
        if (invoice && id && invoice.invoiceType === 'return_slip' && lastInitialTabInvoiceIdRef.current !== id) {
            lastInitialTabInvoiceIdRef.current = id;
            setLineItemsTabIndex(3);
        }
    }, [invoice, id]);

    useEffect(() => {
        if (!invoice || !id) return;
        const orig = invoice.originalExtracted as { invoiceType?: string } | undefined;
        if (!orig || orig.invoiceType !== undefined) return;
        if (initialInvoiceTypeRef.current?.invoiceId === id) return;
        initialInvoiceTypeRef.current = { invoiceId: id, value: invoice.invoiceType ?? 'received' };
    }, [invoice, id]);

    const isCurrentReturnInvoice = isReturnInvoiceType(editedFields.invoiceType ?? invoice?.invoiceType ?? 'received');

    const currentLocationId = useMemo(() => {
        const v = editedFields.locationId ?? invoice?.locationId;
        return v != null && String(v).trim() !== '' ? String(v).trim() : undefined;
    }, [editedFields.locationId, invoice?.locationId]);
    const currentInvoiceType = (editedFields.invoiceType ?? invoice?.invoiceType ?? 'received') as InvoiceType;
    const locationDefaultTaxRate = useMemo(
        () =>
            resolveLocationDefaultTaxRate({
                locationDefaultTaxRates,
                locationId: currentLocationId,
                invoiceType: currentInvoiceType,
            }),
        [locationDefaultTaxRates, currentLocationId, currentInvoiceType],
    );
    const locationTaxEntry = useMemo(
        () => (locationDefaultTaxRates ?? []).find((entry) => entry.locationId === currentLocationId),
        [locationDefaultTaxRates, currentLocationId],
    );
    const isCanadianShop = useMemo(
        () => isCanadianLocation(shopLocations.find((location) => location._id === currentLocationId)?.state),
        [shopLocations, currentLocationId],
    );
    const nexTaxFormat = ((editedFields.nexTaxFormat ?? invoice?.nexTaxFormat) || 'single') as 'single' | 'split';
    const showCanadianTaxControls =
        nexTaxFormat === 'split' ||
        invoice?.nexTaxFormat === 'split' ||
        invoice?.pstTaxTotal != null ||
        editedFields.pstTaxTotal != null ||
        locationTaxEntry?.pstTaxRate != null;
    const taxAutoContextKey = `${currentLocationId ?? ''}:${currentInvoiceType}`;
    const syncTaxManualOverrideFromSources = useCallback(
        (source: {
            lineItems?: InvoiceDetailResponse['lineItems'];
            freightItems?: InvoiceDetailResponse['freightItems'];
            fuelItems?: InvoiceDetailResponse['fuelItems'];
            restockingFeeItems?: InvoiceDetailResponse['restockingFeeItems'];
            taxesTotal?: number;
            discountTotal?: number;
            orderTotal?: number;
            freightTotal?: number;
            pstTaxTotal?: number;
            nexTaxFormat?: InvoiceDetailResponse['nexTaxFormat'];
            locationId?: string;
            invoiceType?: InvoiceType;
            paySettlementType?: string;
        }) => {
            const locationId =
                source.locationId != null && String(source.locationId).trim() !== ''
                    ? String(source.locationId).trim()
                    : undefined;
            const rate = resolveLocationDefaultTaxRate({
                locationDefaultTaxRates,
                locationId,
                invoiceType: (source.invoiceType ?? 'received') as InvoiceType,
            });
            if (rate == null) {
                setTaxManualOverride(false);
                return;
            }
            const subtotal = computeAboveLineItemsSubtotalFromInvoice(source);
            const canadian = isCanadianLocation(
                shopLocations.find((location) => location._id === locationId)?.state,
            );
            const pstTaxTotal =
                source.nexTaxFormat === 'split' && typeof source.pstTaxTotal === 'number' ? source.pstTaxTotal : 0;
            const headerFreightForTotal =
                (source.freightItems?.length ?? 0) > 0
                    ? 0
                    : typeof source.freightTotal === 'number'
                      ? source.freightTotal
                      : 0;
            const raw = computeLocationDefaultTax(rate, subtotal);
            const adjusted = locationDefaultTaxForGrandTotal({
                taxRate: rate,
                aboveLineItemsSubtotal: subtotal,
                isCanadian: canadian,
                pstTaxTotal,
                discountTotal: typeof source.discountTotal === 'number' ? source.discountTotal : 0,
                headerFreightForTotal,
                extractedTotal: typeof source.orderTotal === 'number' ? source.orderTotal : Number.NaN,
            });
            const stored = typeof source.taxesTotal === 'number' ? source.taxesTotal : 0;
            const matchesAutomaticTax =
                Math.abs(stored - raw) <= LOCATION_DEFAULT_TAX_COMPARE_TOLERANCE ||
                Math.abs(stored - adjusted) <= LOCATION_DEFAULT_TAX_COMPARE_TOLERANCE;
            setTaxManualOverride(!matchesAutomaticTax);
        },
        [locationDefaultTaxRates, shopLocations],
    );
    useEffect(() => {
        if (taxAutoContextRef.current === taxAutoContextKey) return;
        taxAutoContextRef.current = taxAutoContextKey;
        setTaxManualOverride(false);
    }, [taxAutoContextKey]);
    useEffect(() => {
        if (!invoice || !id || taxUserEditedRef.current) return;
        const readyKey = `${id}:${isCanadianShop ? 'ca' : 'other'}:${shopLocations.length}`;
        if (taxOverrideInitedRef.current === readyKey) return;
        taxOverrideInitedRef.current = readyKey;
        taxAutoContextRef.current = `${String(invoice.locationId ?? '').trim()}:${invoice.invoiceType ?? 'received'}`;
        syncTaxManualOverrideFromSources(invoice);
    }, [invoice, id, isCanadianShop, shopLocations.length, syncTaxManualOverrideFromSources]);

    const normalizeReturnLineFieldValue = (field: LineItemField, value: unknown): unknown => {
        if (field === 'unitBuyPrice') {
            const n = Number(value);
            return Number.isFinite(n) ? Math.abs(n) : value;
        }
        if (!isCurrentReturnInvoice) return value;
        return value;
    };

    const updateField = useCallback((field: keyof InvoiceDetailResponse, value: unknown) => {
        const shouldForceNegativeSummary =
            isCurrentReturnInvoice &&
            (field === 'subtotal' ||
                field === 'taxesTotal' ||
                field === 'orderTotal' ||
                field === 'freightTotal' ||
                field === 'fuelTotal' ||
                field === 'restockingFee');
        const normalizedValue = shouldForceNegativeSummary ? normalizeReturnSignedNumber(value) : value;
        setEditedFields((prev) => ({ ...prev, [field]: normalizedValue }));
    }, [isCurrentReturnInvoice]);

    /**
     * Cash vs Charge: misc CC/surcharge rows stay in `restockingFeeItems`; UI hides them for Charge.
     * Adjust `orderTotal` by ±sum(credit card misc lines) so the extracted total matches the active pay mode
     * (charge total vs cash total including card fee).
     */
    const setPaySettlementType = useCallback(
        (next: 'cash' | 'charge') => {
            setEditedFields((prev) => {
                const misc = prev.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
                const sumCc = sumCreditCardMiscFeeLineTotals(misc);
                const prevPay: 'cash' | 'charge' =
                    (prev.paySettlementType ?? invoice?.paySettlementType) === 'charge' ? 'charge' : 'cash';
                const ot =
                    typeof prev.orderTotal === 'number'
                        ? prev.orderTotal
                        : typeof invoice?.orderTotal === 'number'
                          ? invoice.orderTotal
                          : 0;
                let nextOrderTotal = ot;
                if (prevPay !== next && Math.abs(sumCc) > 0.001) {
                    nextOrderTotal = next === 'cash' ? ot + sumCc : ot - sumCc;
                }
                return { ...prev, paySettlementType: next, orderTotal: nextOrderTotal };
            });
        },
        [invoice?.restockingFeeItems, invoice?.orderTotal, invoice?.paySettlementType]
    );

    const newLineItem = useCallback(
        (): InvoiceDetailResponse['lineItems'][0] => ({
            title: '',
            partNumber: '',
            qty: isCurrentReturnInvoice ? -1 : 1,
            listPrice: 0,
            unitBuyPrice: 0,
            totalBuyPrice: 0,
            confidence: 0,
        }),
        [isCurrentReturnInvoice]
    );

    const shiftReconciliationIndicesAfterInsert = useCallback((insertedGlobalIndex: number) => {
        const recon = getReconciliation();
        if (!recon) return;
        recon.setUnmatchedPartIndices((prev) => {
            const next = new Set<number>();
            prev.forEach((idx) => next.add(idx >= insertedGlobalIndex ? idx + 1 : idx));
            return next;
        });
        recon.setLocalMatchedNexOverrides((prev) =>
            Object.fromEntries(
                Object.entries(prev).map(([idxStr, nexLine]) => {
                    const idx = parseInt(idxStr, 10);
                    const nextIdx = Number.isNaN(idx) ? idxStr : String(idx >= insertedGlobalIndex ? idx + 1 : idx);
                    return [nextIdx, nexLine];
                })
            )
        );
        recon.setPreviouslyMatchedNexLines((prev) =>
            Object.fromEntries(
                Object.entries(prev).map(([idxStr, nexLine]) => {
                    const idx = parseInt(idxStr, 10);
                    const nextIdx = Number.isNaN(idx) ? idxStr : String(idx >= insertedGlobalIndex ? idx + 1 : idx);
                    return [nextIdx, nexLine];
                })
            )
        );
    }, [getReconciliation]);

    const shiftReconciliationIndicesAfterDelete = useCallback((deletedIndex: number) => {
        const recon = getReconciliation();
        if (!recon) return;
        const remapRecord = <T,>(prev: Record<string, T>): Record<string, T> => {
            const next: Record<string, T> = {};
            for (const [idxStr, value] of Object.entries(prev)) {
                const idx = parseInt(idxStr, 10);
                if (Number.isNaN(idx)) {
                    next[idxStr] = value;
                    continue;
                }
                const mapped = remapCurrentIndexAfterDelete(deletedIndex, idx);
                if (mapped == null) continue;
                next[String(mapped)] = value;
            }
            return next;
        };
        recon.setUnmatchedPartIndices((prev) => {
            const next = new Set<number>();
            prev.forEach((idx) => {
                const mapped = remapCurrentIndexAfterDelete(deletedIndex, idx);
                if (mapped != null) next.add(mapped);
            });
            return next;
        });
        recon.setLocalMatchedNexOverrides((prev) => remapRecord(prev));
        recon.setPreviouslyMatchedNexLines((prev) => remapRecord(prev));
        recon.setExpandedMatchedNexRows((prev) => {
            const next = new Set<string>();
            prev.forEach((key) => {
                const pipe = key.indexOf('|');
                if (pipe <= 0) {
                    next.add(key);
                    return;
                }
                const oldIdx = parseInt(key.slice(0, pipe), 10);
                if (Number.isNaN(oldIdx)) {
                    next.add(key);
                    return;
                }
                const mapped = remapCurrentIndexAfterDelete(deletedIndex, oldIdx);
                if (mapped == null) return;
                next.add(`${mapped}${key.slice(pipe)}`);
            });
            return next;
        });
        setManualPoHeaderSelections((prev) => {
            const next: Record<number, number> = {};
            for (const [kStr, value] of Object.entries(prev)) {
                const idx = parseInt(kStr, 10);
                if (Number.isNaN(idx) || typeof value !== 'number' || !Number.isFinite(value)) continue;
                if (idx < 0) {
                    next[idx] = value;
                    continue;
                }
                const mapped = remapCurrentIndexAfterDelete(deletedIndex, idx);
                if (mapped == null) continue;
                next[mapped] = value;
            }
            return next;
        });
    }, [getReconciliation]);

    const pinnedLineBackendMapRef = useRef<{
        items: InvoiceDetailResponse['lineItems'];
        map: (number | undefined)[];
    } | null>(null);
    const activeLineBackendMapRef = useRef<(number | undefined)[]>([]);
    const reviseLineBackendMapRef = useRef<
        (
            nextItems: InvoiceDetailResponse['lineItems'],
            revise: (prev: readonly (number | undefined)[]) => (number | undefined)[],
        ) => void
    >(() => {});
    const reviseLineBackendMap = useCallback(
        (
            nextItems: InvoiceDetailResponse['lineItems'],
            revise: (prev: readonly (number | undefined)[]) => (number | undefined)[],
        ) => {
            reviseLineBackendMapRef.current(nextItems, revise);
        },
        [],
    );

    const {
        updateLineItem: updateLineItemBase,
        restoreLineItemRowsSnapshot,
        addLineItem,
        addLineItemAbove,
        addLineItemAt,
        duplicateLineItemAt,
        removeLineItem,
        getLineItemValue,
        getLineItemStringValue,
        getLineItemPriceValue,
        updateFreightItem,
        addFreightItem,
        addFreightItemAt,
        addFreightItemAbove,
        duplicateFreightItemAt,
        removeFreightItem,
        getFreightItemValue,
        getFreightItemStringValue,
        getFreightItemPriceValue,
        updateFuelItem,
        addFuelItem,
        addFuelItemAt,
        removeFuelItem,
        getFuelItemValue,
        getFuelItemStringValue,
        getFuelItemPriceValue,
        updateRestockingFeeItem,
        addRestockingFeeItem,
        addRestockingFeeItemAt,
        addRestockingFeeItemAbove,
        duplicateRestockingFeeItemAt,
        removeRestockingFeeItem,
        getRestockingFeeItemValue,
        getRestockingFeeItemStringValue,
        getRestockingFeeItemPriceValue,
        addIgnoredPart,
        addIgnoredPartAt,
        addIgnoredPartAbove,
        removeIgnoredPart,
        updateIgnoredPart,
        restoreIgnoredPart,
        getIgnoredPartValue,
        getIgnoredPartStringValue,
        getIgnoredPartPriceValue,
    } = useInvoiceDetailFormPartTabs({
        invoice,
        editedFields,
        setEditedFields,
        newLineItem,
        normalizeReturnLineFieldValue,
        shiftReconciliationIndicesAfterInsert,
        shiftReconciliationIndicesAfterDelete,
        reviseLineBackendMap,
    });

    const updateLineItem = useCallback(
        (index: number, field: LineItemField, value: unknown) => {
            const items = editedFields.lineItems ?? invoice?.lineItems ?? [];
            if (
                isSalesTaxLineItemPart(items[index]) &&
                (field === 'totalBuyPrice' || field === 'unitBuyPrice' || field === 'qty')
            ) {
                setTaxManualOverride(true);
            }
            updateLineItemBase(index, field, value);
        },
        [editedFields.lineItems, invoice?.lineItems, updateLineItemBase],
    );

    const {
        reorderLineItems,
        reorderFreightItems,
        reorderRestockingFeeItems,
        reorderIgnoredParts,
    } = useInvoiceDetailFormReorder({
        invoice,
        editedFields,
        getReconciliation,
        setEditedFields,
        setEditingNumericField,
        setEditingNumericValues,
        setManualPoHeaderSelections,
        reviseLineBackendMap,
    });

    const getDisplayValue = useCallback((field: keyof InvoiceDetailResponse) => {
        if (field === 'locationId') {
            const v = editedFields.locationId ?? invoice?.locationId;
            return v != null && String(v).trim() !== '' ? String(v).trim() : '';
        }
        if (field === 'paySettlementType') {
            const v = editedFields.paySettlementType ?? invoice?.paySettlementType;
            return v === 'charge' ? 'charge' : 'cash';
        }
        if (field === 'discountTotal') {
            if (editedFields.discountTotal !== undefined) return editedFields.discountTotal;
            return typeof invoice?.discountTotal === 'number' ? invoice.discountTotal : 0;
        }
        return editedFields[field] !== undefined ? editedFields[field] : (invoice ? invoice[field] : '');
    }, [editedFields, invoice]);
    const getStringValue = (field: keyof InvoiceDetailResponse) => (typeof getDisplayValue(field) === 'string' ? getDisplayValue(field) : '') as string;
    const getNumberValue = useCallback(
        (field: keyof InvoiceDetailResponse) => (typeof getDisplayValue(field) === 'number' ? getDisplayValue(field) : 0) as number,
        [getDisplayValue]
    );

    /** Parsed value from an open numeric cell draft, or fallback when not editing / incomplete input. */
    const parseDraftNumeric = useCallback((cellKey: string, fallback: number): number => {
        if (editingNumericField !== cellKey) return fallback;
        const raw = editingNumericValues[cellKey];
        if (raw === undefined) return fallback;
        const t = raw.trim();
        if (t === '' || t === '-' || t === '.' || t === '-.') return fallback;
        const n = parseFloat(t);
        return Number.isFinite(n) ? n : fallback;
    }, [editingNumericField, editingNumericValues]);

    /** Header totals: use in-progress input so arithmetic flags update before blur/save. */
    const getSummaryNumberWhileEditing = useCallback(
        (fieldName: keyof InvoiceDetailResponse): number => {
            const fieldKey = `summary-${String(fieldName)}`;
            if (fieldName === 'discountTotal') {
                if (editingNumericField !== fieldKey) return getNumberValue(fieldName);
                const raw = editingNumericValues[fieldKey];
                if (raw === undefined) return getNumberValue(fieldName);
                const t = raw.trim();
                if (t === '' || t === '.' || t === '-') return getNumberValue(fieldName);
                const n = parseFloat(t);
                if (!Number.isFinite(n)) return getNumberValue(fieldName);
                if (n === 0) return 0;
                return isCurrentReturnInvoice ? Math.abs(n) : -Math.abs(n);
            }
            if (fieldName === 'orderTotal' && isCurrentReturnInvoice) {
                if (editingNumericField !== fieldKey) return getNumberValue(fieldName);
                const raw = editingNumericValues[fieldKey];
                if (raw === undefined) return getNumberValue(fieldName);
                const t = raw.trim();
                if (t === '' || t === '.' || t === '-') return getNumberValue(fieldName);
                const n = parseFloat(t);
                if (!Number.isFinite(n)) return getNumberValue(fieldName);
                return n === 0 ? 0 : -Math.abs(n);
            }
            if (fieldName === 'taxesTotal' && isCurrentReturnInvoice) {
                if (editingNumericField !== fieldKey) return getNumberValue(fieldName);
                const raw = editingNumericValues[fieldKey];
                if (raw === undefined) return getNumberValue(fieldName);
                const t = raw.trim();
                if (t === '' || t === '.' || t === '-') return getNumberValue(fieldName);
                const n = parseFloat(t);
                if (!Number.isFinite(n)) return getNumberValue(fieldName);
                return n === 0 ? 0 : -Math.abs(n);
            }
            if (editingNumericField !== fieldKey) return getNumberValue(fieldName);
            const raw = editingNumericValues[fieldKey];
            if (raw === undefined) return getNumberValue(fieldName);
            const t = raw.trim();
            if (t === '' || t === '-' || t === '.' || t === '-.') return getNumberValue(fieldName);
            const n = parseFloat(t);
            return Number.isFinite(n) ? n : getNumberValue(fieldName);
        },
        [editingNumericField, editingNumericValues, isCurrentReturnInvoice, getNumberValue]
    );

    const parseInvoiceDateForDisplay = (val: Date | string | undefined): Date | null => {
        if (!val) return null;
        if (val instanceof Date && !isNaN(val.getTime())) return val;
        const str = String(val);
        const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        const d = new Date(str);
        return isNaN(d.getTime()) ? null : d;
    };

    const getDateValue = useCallback((): Date => {
        const v = editedFields.timestamp ?? invoice?.timestamp;
        const p = parseInvoiceDateForDisplay(v instanceof Date ? v : (v as string | undefined));
        if (p) return p;
        if (invoice?.timestamp) {
            const ip = parseInvoiceDateForDisplay(invoice.timestamp instanceof Date ? invoice.timestamp : (invoice.timestamp as string));
            if (ip) return ip;
        }
        return new Date();
    }, [editedFields.timestamp, invoice?.timestamp]);

    const orig = invoice?.originalExtracted;
    const getOriginalInvoiceType = (): InvoiceType | undefined => {
        if (!orig || !id) return undefined;
        const fromOrig = (orig as { invoiceType?: InvoiceType }).invoiceType;
        if (fromOrig !== undefined) return fromOrig;
        if (initialInvoiceTypeRef.current?.invoiceId === id) return initialInvoiceTypeRef.current.value as InvoiceType;
        return undefined;
    };
    const getOriginalValue = (field: keyof InvoiceDetailResponse): string | number | Date | undefined => {
        if (!orig) return undefined;
        if (field === 'lineItems' || field === 'ignoredParts' || field === 'image' || field === 'reconciliationIssues') return undefined;
        if (field === 'invoiceType') return getOriginalInvoiceType();
        if (field === 'paySettlementType') {
            const v = orig.paySettlementType;
            return v === 'charge' ? 'charge' : 'cash';
        }
        if (field === 'locationId') {
            const fromOrig = (orig as { locationId?: string }).locationId;
            if (fromOrig != null && String(fromOrig).trim() !== '') return String(fromOrig).trim();
            const fromInvoice = invoice?.locationId;
            return fromInvoice != null && String(fromInvoice).trim() !== '' ? String(fromInvoice).trim() : undefined;
        }
        if (field === 'locationTitle') {
            const fromOrig = (orig as { locationTitle?: string }).locationTitle;
            if (fromOrig != null && String(fromOrig).trim() !== '') return String(fromOrig).trim();
            const fromInvoice = invoice?.locationTitle;
            return fromInvoice != null && String(fromInvoice).trim() !== '' ? String(fromInvoice).trim() : undefined;
        }
        return orig[field as keyof typeof orig] as string | number | Date | undefined;
    };

    const syntheticEmptyLineItem = useMemo(() => newLineItem(), [newLineItem]);
    const currentLineItems = useMemo(() => editedFields.lineItems ?? invoice?.lineItems ?? [], [editedFields.lineItems, invoice?.lineItems]);

    useEffect(() => {
        if (!invoice || !id) return;
        if (normalizedUnitPriceInvoiceIdRef.current === id) return;
        if (Object.keys(editedFields).length > 0) return;

        const nextLineItems = normalizeItemsUnitPriceFromAmountAndQty(invoice.lineItems ?? []);
        const nextFreightItems = normalizeItemsUnitPriceFromAmountAndQty(invoice.freightItems ?? []);
        const nextFuelItems = normalizeItemsUnitPriceFromAmountAndQty(invoice.fuelItems ?? []);
        const nextRestockingFeeItems = normalizeItemsUnitPriceFromAmountAndQty(invoice.restockingFeeItems ?? []);
        const nextIgnoredParts = normalizeItemsUnitPriceFromAmountAndQty(invoice.ignoredParts ?? []);

        const hasAnyChanges =
            JSON.stringify(nextLineItems) !== JSON.stringify(invoice.lineItems ?? []) ||
            JSON.stringify(nextFreightItems) !== JSON.stringify(invoice.freightItems ?? []) ||
            JSON.stringify(nextFuelItems) !== JSON.stringify(invoice.fuelItems ?? []) ||
            JSON.stringify(nextRestockingFeeItems) !== JSON.stringify(invoice.restockingFeeItems ?? []) ||
            JSON.stringify(nextIgnoredParts) !== JSON.stringify(invoice.ignoredParts ?? []);

        normalizedUnitPriceInvoiceIdRef.current = id;
        if (!hasAnyChanges) return;

        setEditedFields((prev) => ({
            ...prev,
            lineItems: nextLineItems,
            freightItems: nextFreightItems,
            fuelItems: nextFuelItems,
            restockingFeeItems: nextRestockingFeeItems,
            ignoredParts: nextIgnoredParts,
        }));
    }, [editedFields, id, invoice]);

    const localLineArithmeticIssues = useMemo(
        () => computeLocalLineArithmeticIssues({ editedFields, invoice, parseDraftNumeric }),
        [editedFields, invoice, parseDraftNumeric],
    );

    const lineItemBackendIndexByCurrentIndex = useMemo(() => {
        const pinned = pinnedLineBackendMapRef.current;
        if (pinned && pinned.items === currentLineItems) return pinned.map;
        return computeLineItemBackendIndexByCurrentIndex(invoice?.lineItems ?? [], currentLineItems);
    }, [invoice?.lineItems, currentLineItems]);
    if (pinnedLineBackendMapRef.current && pinnedLineBackendMapRef.current.items !== currentLineItems) {
        pinnedLineBackendMapRef.current = null;
    }
    activeLineBackendMapRef.current = lineItemBackendIndexByCurrentIndex;
    reviseLineBackendMapRef.current = (nextItems, revise) => {
        const nextMap = revise(activeLineBackendMapRef.current);
        if (nextMap.length !== nextItems.length) {
            pinnedLineBackendMapRef.current = null;
            return;
        }
        pinnedLineBackendMapRef.current = { items: nextItems, map: nextMap };
        activeLineBackendMapRef.current = nextMap;
    };

    const lineItemOriginalByIndex = useMemo(
        () => (orig?.lineItems ? computeLineItemOriginalByIndex(orig, currentLineItems, syntheticEmptyLineItem) : []),
        [orig, currentLineItems, syntheticEmptyLineItem]
    );

    const findOriginalLineItem = (index: number): InvoiceDetailResponse['lineItems'][0] | undefined =>
        lineItemOriginalByIndex[index]?.origItem ?? syntheticEmptyLineItem;
    const getOriginalLineItemValue = (index: number, field: LineItemField) => findOriginalLineItem(index)?.[field];

    const normalizeForCompare = (a: unknown, b: unknown): boolean => {
        if (a === b) return true;
        if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
        if (typeof a === 'string' && typeof b === 'string') return a.trim() === b.trim();
        if (typeof a === 'number' && typeof b === 'number') {
            if (Number.isNaN(a) || Number.isNaN(b)) return Math.abs((Number.isNaN(a) ? 0 : a) - (Number.isNaN(b) ? 0 : b)) < 0.005;
            return Math.abs(a - b) < 0.005;
        }
        if (typeof a === 'string' && typeof b === 'number') return !Number.isNaN(parseFloat(a)) && Math.abs(parseFloat(a) - b) < 0.005;
        if (typeof a === 'number' && typeof b === 'string') return !Number.isNaN(parseFloat(b)) && Math.abs(a - parseFloat(b)) < 0.005;
        const empty = (v: unknown) => v == null || v === '';
        const zero = (v: unknown) => empty(v) || (typeof v === 'number' && (Number.isNaN(v) || Math.abs(v) < 0.005)) || (typeof v === 'string' && (v.trim() === '' || Math.abs(parseFloat(v)) < 0.005));
        if (zero(a) && zero(b)) return true;
        return false;
    };

    const isFieldEdited = (field: keyof InvoiceDetailResponse): boolean => {
        if (!orig) return false;
        const origVal = getOriginalValue(field);
        if (origVal === undefined) return false;
        return !normalizeForCompare(getDisplayValue(field), origVal);
    };

    const isLineItemFieldEdited = (index: number, field: LineItemField): boolean => {
        if (!orig?.lineItems) return false;
        const origVal = getOriginalLineItemValue(index, field);
        if (origVal === undefined) return false;
        return !normalizeForCompare(getLineItemValue(index, field), origVal);
    };

    const getLineItemEditedFieldLabels = (index: number): string[] =>
        lineItemEditableFields.filter((f) => isLineItemFieldEdited(index, f)).map((f) => lineItemFieldLabelMap[f] ?? f);
    const hasAnyLineItemFieldEdited = (index: number) => getLineItemEditedFieldLabels(index).length > 0;

    const formatValueForModal = (value: unknown, fieldKey: string): string => {
        if (fieldKey === 'locationId') return value != null && String(value).trim() !== '' ? String(value).trim() : '';
        if (fieldKey === 'invoiceType') {
            if (value === 'returned') return 'Credited';
            if (value === 'return_slip') return 'Return Slip';
            if (value === 'sublet') return 'Sublet';
            return 'Received';
        }
        if (fieldKey === 'paySettlementType') return value === 'charge' ? 'Charge' : 'Cash';
        if (fieldKey === 'ccVendorId' || fieldKey === 'ccVendorName') {
            if (value == null || value === '') return 'No credit card';
            return String(value);
        }
        if (value instanceof Date && !isNaN(value.getTime())) return value.toLocaleDateString();
        if (typeof value === 'number') return fieldKey.includes('Total') || fieldKey.includes('Price') || fieldKey.includes('Fee') || fieldKey.includes('subtotal') || fieldKey.includes('freight') || fieldKey.includes('taxes') || fieldKey.includes('fuel') ? (value < 0 ? `-$${fmtUsd(Math.abs(value))}` : `$${fmtUsd(value)}`) : String(value);
        if (value == null) return '';
        return String(value);
    };

    const openRevertModal = (fieldKey: string, fieldLabel: string) => setRevertModal({ fieldKey, fieldLabel });
    const closeRevertModal = () => setRevertModal(null);

    const handleRevertFromModal = () => {
        if (!revertModal || !id) return;
        const { fieldKey } = revertModal;
        closeRevertModal();
        if (fieldKey.startsWith('lineItem-')) {
            const m = fieldKey.match(/^lineItem-(\d+)-(.+)$/);
            if (m) {
                const index = parseInt(m[1], 10);
                const field = m[2] as LineItemField;
                const original = getOriginalLineItemValue(index, field);
                if (original === undefined) return;
                const items = editedFields.lineItems ?? invoice?.lineItems ?? [];
                setEditedFields((prev) => ({
                    ...prev,
                    lineItems: items.map((item, i) => (i === index ? { ...item, [field]: original } : item)),
                }));
            }
        } else {
            const field = fieldKey as keyof InvoiceDetailResponse;
            if (field === 'invoiceType') {
                updateField('invoiceType', invoice?.invoiceType ?? 'received');
                return;
            }
            if (field === 'paySettlementType') {
                const o = getOriginalValue('paySettlementType');
                if (o === 'charge' || o === 'cash') setPaySettlementType(o);
                return;
            }
            if (field === 'ccVendorId' || field === 'ccVendorName') {
                updateField('ccVendorId', invoice?.ccVendorId ?? null);
                updateField('ccVendorName', invoice?.ccVendorName ?? null);
                return;
            }
            if (field === 'locationId') {
                const oid = getOriginalValue('locationId');
                if (oid === undefined) return;
                const ttl = getOriginalValue('locationTitle');
                updateField('locationId', oid);
                updateField(
                    'locationTitle',
                    ttl != null && String(ttl).trim() !== ''
                        ? String(ttl).trim()
                        : String(oid) === String(invoice?.locationId ?? '').trim()
                          ? String(invoice?.locationTitle ?? '').trim()
                          : ''
                );
                return;
            }
            const original = getOriginalValue(field);
            if (original === undefined) return;
            if (field === 'taxesTotal') setTaxManualOverride(false);
            updateField(field, original);
        }
    };

    const getRevertModalOriginalValue = (): string => {
        if (!revertModal) return '';
        if (revertModal.fieldKey.startsWith('lineItem-')) {
            const m = revertModal.fieldKey.match(/^lineItem-(\d+)-(.+)$/);
            if (m) return formatValueForModal(getOriginalLineItemValue(parseInt(m[1], 10), m[2] as LineItemField), revertModal.fieldKey);
        }
        if (revertModal.fieldKey === 'invoiceType') return formatValueForModal(invoice?.invoiceType ?? 'received', revertModal.fieldKey);
        if (revertModal.fieldKey === 'ccVendorId' || revertModal.fieldKey === 'ccVendorName') {
            const name = invoice?.ccVendorName;
            const id = invoice?.ccVendorId;
            return formatValueForModal(
                name != null && String(name).trim() !== ''
                    ? String(name).trim()
                    : id != null
                      ? `Vendor #${id}`
                      : null,
                'ccVendorName'
            );
        }
        if (revertModal.fieldKey === 'locationId') {
            const t = getOriginalValue('locationTitle');
            const id = getOriginalValue('locationId');
            return formatValueForModal(
                t != null && String(t).trim() !== '' ? String(t).trim() : id,
                revertModal.fieldKey
            );
        }
        return formatValueForModal(getOriginalValue(revertModal.fieldKey as keyof InvoiceDetailResponse), revertModal.fieldKey);
    };

    const getRevertModalCurrentValue = (): string => {
        if (!revertModal) return '';
        if (revertModal.fieldKey.startsWith('lineItem-')) {
            const m = revertModal.fieldKey.match(/^lineItem-(\d+)-(.+)$/);
            if (m) return formatValueForModal(getLineItemValue(parseInt(m[1], 10), m[2] as LineItemField), revertModal.fieldKey);
        }
        if (revertModal.fieldKey === 'locationId') {
            const t = getDisplayValue('locationTitle');
            const id = getDisplayValue('locationId');
            return formatValueForModal(
                typeof t === 'string' && t.trim() !== '' ? t.trim() : id,
                revertModal.fieldKey
            );
        }
        if (revertModal.fieldKey === 'ccVendorId' || revertModal.fieldKey === 'ccVendorName') {
            const name = getDisplayValue('ccVendorName');
            const id = getDisplayValue('ccVendorId');
            return formatValueForModal(
                typeof name === 'string' && name.trim() !== ''
                    ? name.trim()
                    : id != null
                      ? `Vendor #${id}`
                      : null,
                'ccVendorName'
            );
        }
        return formatValueForModal(getDisplayValue(revertModal.fieldKey as keyof InvoiceDetailResponse), revertModal.fieldKey);
    };

    /**
     * `invoice.reconciliationIssues.*.partIndex` is stored against the last-saved row layout.
     * After local insert/delete/reorder of line items, map those indices to the current edited layout
     * (same rules as `buildCurrentReconciliationIssuesForSave`) so submit preview matches the line-items UI.
     */
    const remapSubmitPreviewPartIndex = useCallback(
        (oldIndex: number): number | undefined => {
            const layout = buildPartIndexRemapLayout(invoice, editedFields, lineItemBackendIndexByCurrentIndex);
            return remapStoredPartIndex(layout, oldIndex);
        },
        [editedFields, invoice, lineItemBackendIndexByCurrentIndex],
    );

    const recon = getReconciliation();

    const getNexSubmitComputeArgs = useCallback(() => {
        if (!invoice) return null;
        return buildNexSubmitComputeArgs({
            invoice,
            editedFields,
            nexReconciliationIdentifierMode,
            recon,
            preservedMatchedNexLines: preservedReconciliationRef.current.matchedNexLines,
            remapSubmitPreviewPartIndex: (idx) => remapSubmitPreviewPartIndex(idx) ?? null,
            salesTaxAsLineItem,
        });
    }, [invoice, editedFields, recon, remapSubmitPreviewPartIndex, nexReconciliationIdentifierMode, salesTaxAsLineItem]);

    const hasChanges = () =>
        Object.keys(editedFields).length > 0 || (recon?.unmatchedPartIndices?.size ?? 0) > 0 || (recon ? Object.keys(recon.localMatchedNexOverrides).length > 0 : false);

    const canReset = (): boolean => {
        if (!invoice) return false;
        if ((recon?.unmatchedPartIndices?.size ?? 0) > 0 || (recon ? Object.keys(recon.localMatchedNexOverrides).length > 0 : false)) return true;
        if (!invoice.originalExtracted) return hasChanges();
        const o = invoice.originalExtracted;
        const lineItems = editedFields.lineItems ?? invoice.lineItems ?? [];
        if ((lineItems?.length ?? 0) !== (o.lineItems?.length ?? 0)) return true;
        if ((editedFields.freightItems ?? invoice.freightItems ?? []).length !== (o.freightItems ?? []).length) return true;
        if ((editedFields.fuelItems ?? invoice.fuelItems ?? []).length !== (o.fuelItems ?? []).length) return true;
        if ((editedFields.restockingFeeItems ?? invoice.restockingFeeItems ?? []).length !== (o.restockingFeeItems ?? []).length) return true;
        if ((editedFields.ignoredParts ?? invoice.ignoredParts ?? []).length !== (o.ignoredParts ?? []).length) return true;
        const topLevel: (keyof InvoiceDetailResponse)[] = ['invoiceNumber', 'roNumber', 'purchaseOrderNumber', 'shopRoNumber', 'timestamp', 'supplierText', 'invoiceType', 'paySettlementType', 'ccVendorId', 'ccVendorName', 'locationId', 'orderTotal', 'subtotal', 'freightTotal', 'taxesTotal', 'fuelTotal', 'restockingFee', 'discountTotal', 'nexTaxFormat', 'pstTaxTotal'];
        for (const f of topLevel) if (isFieldEdited(f)) return true;
        for (let i = 0; i < (lineItems?.length ?? 0); i++) for (const f of lineItemEditableFields) if (isLineItemFieldEdited(i, f)) return true;
        return false;
    };

    const getResetPayload = (): Partial<InvoiceDetailResponse> =>
        getResetPayloadFromOriginal({ invoice, orig, originalInvoiceType: getOriginalInvoiceType() });

    const buildReconciliationIssuesForSave = (): InvoiceDetailResponse['reconciliationIssues'] | undefined =>
        buildReconciliationIssuesPayload({
            invoice,
            editedFields,
            lineItemBackendIndexByCurrentIndex,
            preservedReconciliation: preservedReconciliationRef.current,
        });

    /** Same as reset-to-original, but forces a new invoice type and clears all post-extraction reconciliation state. */
    const getInvoiceTypeChangeResetPayload = useCallback(
        (nextType: InvoiceType) => buildInvoiceTypeChangeResetPayload(invoice, nextType),
        [invoice],
    );

    /** When no extraction snapshot exists, re-save current persisted invoice rows with the new type and cleared matches. */
    const getInvoiceTypeChangeFallbackPayload = useCallback(
        (nextType: InvoiceType) => buildInvoiceTypeChangeFallbackPayload(invoice, nextType),
        [invoice],
    );

    const handleResetClick = () => setResetConfirmOpen(true);
    const buildNexSubmitPreviewArgs = useCallback(() => {
        const base = getNexSubmitComputeArgs();
        if (!base) return null;

        const matchedPartIndices = new Set(base.matchedNexLines.map((m) => m.partIndex));
        for (const idx of base.manualMatchedPartIndices) matchedPartIndices.add(idx);
        const unmatchedPartIndexSet = new Set(base.unmatchedPartIndices);
        const matchEvaluation = evaluateNexLineMatchSubmitForUi({
            lineItems: base.lineItems,
            freightItems: base.freightItems,
            fuelItems: base.fuelItems,
            matchedPartIndices,
            unmatchedPartIndices: unmatchedPartIndexSet,
            unmatchedLineItemsNexSubmitMode,
        });

        return {
            base,
            unmatchedBillablePartIndices: matchEvaluation.unmatchedBillablePartIndices,
            allowNullPoHeaderForUnmatchedBillable:
                allowsManualUnmatchedNexSubmit(unmatchedLineItemsNexSubmitMode) &&
                matchEvaluation.unmatchedBillablePartIndices.length > 0,
        };
    }, [getNexSubmitComputeArgs, unmatchedLineItemsNexSubmitMode]);

    const handleSubmitClick = () => {
        const previewArgs = buildNexSubmitPreviewArgs();
        if (previewArgs) {
            const built = computeNexSubmitApDistPreview({
                ...previewArgs.base,
                manualPoHeaderOverrides: {},
                allowNullPoHeaderForUnmatchedBillable: previewArgs.allowNullPoHeaderForUnmatchedBillable,
                unmatchedBillablePartIndices: previewArgs.unmatchedBillablePartIndices,
            });
            if (built) {
                const unmatchedBillableSet = new Set(previewArgs.unmatchedBillablePartIndices);
                setManualPoHeaderSelections((prev) => {
                    const next = { ...prev };
                    for (const info of built.manualPoLineInfos) {
                        if (unmatchedBillableSet.has(info.globalIndex)) continue;
                        if (info.autoPoHeaderId != null && next[info.globalIndex] === undefined) {
                            next[info.globalIndex] = info.autoPoHeaderId;
                        }
                    }
                    return next;
                });
            }
        }
        setSubmitConfirmOpen(true);
    };
    const handleSubmitConfirm = () => {
        if (!id) return;
        setSubmitConfirmOpen(false);
        const manualMatchesArray = buildManualMatchesFromOverrides(
            recon?.localMatchedNexOverrides ?? {},
            editedFields.invoiceType ?? invoice?.invoiceType,
        );
        const remappedReconciliationIssues = buildReconciliationIssuesForSave();
        const manualPoHeaderOverrides = buildManualPoHeaderOverridesFromSelections(manualPoHeaderSelections);
        const fieldsToSubmit: Partial<InvoiceDetailResponse> = {
            ...editedFields,
            ...(currentLocationId ? { locationId: currentLocationId } : {}),
            ...(recon ? { unmatchedPartIndices: Array.from(recon.unmatchedPartIndices) } : {}),
            manualMatches: manualMatchesArray,
            ...(remappedReconciliationIssues ? { reconciliationIssues: remappedReconciliationIssues } : {}),
        };
        savedFieldsRef.current = { ...fieldsToSubmit };
        updateInvoiceMutation.mutate({
            invoiceId: id,
            markAsManuallySubmitted: true,
            updatedFields: fieldsToSubmit,
            manualPoHeaderOverrides,
        } as UpdateInvoiceInput);
    };

    const handleResetConfirm = () => {
        if (!id || !invoice) return;
        setResetConfirmOpen(false);
        recon?.setUnmatchedPartIndices(new Set());
        recon?.setLocalMatchedNexOverrides({});
        recon?.setPreviouslyMatchedNexLines({});
        const o = invoice.originalExtracted;
        if (o) {
            const payload = getResetPayload();
            setEditedFields(payload);
            syncTaxManualOverrideFromSources({ ...invoice, ...payload });
            isMutatingRef.current = true;
            isAutoSaveRef.current = true;
            savedFieldsRef.current = { ...payload };
            updateInvoiceMutation.mutate({ invoiceId: id, updatedFields: payload } as UpdateInvoiceInput);
            toast({ title: 'Reset and saved', description: 'All fields have been reverted to their original values and saved.', status: 'success', duration: 3000, isClosable: true });
        } else {
            setEditedFields({});
            syncTaxManualOverrideFromSources(invoice);
            toast({ title: 'Edits cleared', description: 'Unsaved edits have been cleared.', status: 'info', duration: 3000, isClosable: true });
        }
    };

    const confirmInvoiceTypeChange = useCallback(
        (nextType: InvoiceType) => {
            if (!id || !invoice) return;
            const r = getReconciliation();
            if (r) {
                r.setUnmatchedPartIndices(new Set());
                r.setLocalMatchedNexOverrides({});
                r.setPreviouslyMatchedNexLines({});
            }
            setManualPoHeaderSelections({});
            const payload =
                invoice.originalExtracted != null
                    ? getInvoiceTypeChangeResetPayload(nextType)
                    : getInvoiceTypeChangeFallbackPayload(nextType);
            isMutatingRef.current = true;
            isAutoSaveRef.current = false;
            savedFieldsRef.current = { ...payload };
            setEditedFields({});
            updateInvoiceMutation.mutate({
                invoiceId: id,
                updatedFields: payload,
            } as UpdateInvoiceInput);
        },
        [
            id,
            invoice,
            getReconciliation,
            updateInvoiceMutation,
            getInvoiceTypeChangeResetPayload,
            getInvoiceTypeChangeFallbackPayload,
        ]
    );

    const confirmShopLocationChange = useCallback(
        (next: { locationId: string; locationTitle: string }) => {
            if (!id || !invoice) return;
            const locationId = String(next.locationId ?? '').trim();
            if (!locationId) return;
            const locationTitle = String(next.locationTitle ?? '').trim();
            const payload: Partial<InvoiceDetailResponse> = {
                locationId,
                locationTitle,
            };
            isMutatingRef.current = true;
            isAutoSaveRef.current = false;
            updateField('locationId', locationId);
            updateField('locationTitle', locationTitle);
            savedFieldsRef.current = { ...editedFields, ...payload };
            updateInvoiceMutation.mutate({
                invoiceId: id,
                updatedFields: payload,
            } as UpdateInvoiceInput);
        },
        [id, invoice, editedFields, updateField, updateInvoiceMutation],
    );

    const confirmVendorChange = useCallback(
        (nextVendorName: string) => {
            if (!id || !invoice) return;
            const trimmedVendor = String(nextVendorName ?? '').trim();
            if (!trimmedVendor) return;
            const payload: Partial<InvoiceDetailResponse> = {
                supplierText: trimmedVendor,
            };
            isMutatingRef.current = true;
            isAutoSaveRef.current = false;
            updateField('supplierText', trimmedVendor);
            savedFieldsRef.current = { ...editedFields, ...payload };
            updateInvoiceMutation.mutate({
                invoiceId: id,
                updatedFields: payload,
            } as UpdateInvoiceInput);
        },
        [id, invoice, editedFields, updateField, updateInvoiceMutation],
    );

    const getCurrentRoNumber = (): string =>
        String(editedFields.roNumber ?? invoice?.roNumber ?? '').trim();

    const getOriginalRoNumber = (): string => String(invoice?.roNumber ?? '').trim();

    const hasRoNumberChanged = (): boolean => getCurrentRoNumber() !== getOriginalRoNumber();

    const handleSave = (opts?: { refreshNexsyisData?: boolean }) => {
        if (!id || !hasChanges()) return;
        isMutatingRef.current = true;
        isAutoSaveRef.current = false;
        const manualMatchesArray = buildManualMatchesFromOverrides(
            recon?.localMatchedNexOverrides ?? {},
            editedFields.invoiceType ?? invoice?.invoiceType,
        );
        const fieldsToSave: Partial<InvoiceDetailResponse> = {
            ...editedFields,
            ...(recon ? { unmatchedPartIndices: Array.from(recon.unmatchedPartIndices) } : {}),
            manualMatches: manualMatchesArray,
            ...(!opts?.refreshNexsyisData ? { reconciliationIssues: buildReconciliationIssuesForSave() } : {}),
        };
        savedFieldsRef.current = { ...fieldsToSave };
        updateInvoiceMutation.mutate({
            invoiceId: id,
            updatedFields: fieldsToSave,
            ...(opts?.refreshNexsyisData ? { refreshNexsyisData: true } : {}),
        } as UpdateInvoiceInput);
    };

    const lineItemsUseDirectIndex = useMemo(() => {
        const backend = invoice?.lineItems ?? [];
        if (backend.length !== currentLineItems.length) return false;
        return lineItemBackendIndexByCurrentIndex.every((bi, i) => bi === i);
    }, [invoice?.lineItems, currentLineItems.length, lineItemBackendIndexByCurrentIndex]);
    const flaggedPartIndices = useMemo(() => {
        const isReturnSlip = (editedFields.invoiceType ?? invoice?.invoiceType) === 'return_slip';
        let backendIssues = filterOutIgnoredTabCalculationMismatches(invoice?.reconciliationIssues?.flaggedParts, {
            lineItems: editedFields.lineItems ?? invoice?.lineItems ?? [],
            freightItems: editedFields.freightItems ?? invoice?.freightItems ?? [],
            fuelItems: editedFields.fuelItems ?? invoice?.fuelItems ?? [],
            restockingFeeItems: editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [],
            ignoredParts: editedFields.ignoredParts ?? invoice?.ignoredParts ?? [],
        });
        if (isReturnSlip) {
            backendIssues = omitReturnSlipAmountFlags(
                backendIssues.filter((i: { issueType?: string }) => i.issueType !== 'calculation_mismatch'),
                'return_slip',
            );
        }
        const backendSet = new Set(backendIssues.map((i: { partIndex: number }) => i.partIndex));
        const liveArithmetic = isReturnSlip ? [] : localLineArithmeticIssues;
        const liveSet = new Set(liveArithmetic.map((i) => i.partIndex));
        const lineCount = (editedFields.lineItems ?? invoice?.lineItems ?? []).length;
        if (lineItemsUseDirectIndex) {
            const combined = new Set<number>(backendSet);
            liveSet.forEach((idx) => combined.add(idx));
            return combined;
        }
        const lineBackendValues = new Set(
            lineItemBackendIndexByCurrentIndex.slice(0, lineCount).filter((x): x is number => x != null)
        );
        const savedLineCount = invoice?.lineItems?.length ?? lineCount;
        const set = new Set<number>();
        for (let ci = 0; ci < lineCount; ci++) {
            const bi = lineItemBackendIndexByCurrentIndex[ci];
            if (bi != null && backendSet.has(bi)) set.add(ci);
        }
        backendSet.forEach((p) => {
            if (lineBackendValues.has(p)) return;
            // Deleted line-item flags stay with the removed row. Re-adding the old
            // index paints that flag onto whatever line slid into the gap.
            if (p >= 0 && p < savedLineCount) return;
            set.add(p);
        });
        liveSet.forEach((idx) => set.add(idx));
        return set;
    }, [
        invoice?.reconciliationIssues,
        editedFields.invoiceType,
        editedFields.lineItems,
        editedFields.freightItems,
        editedFields.fuelItems,
        editedFields.restockingFeeItems,
        editedFields.ignoredParts,
        invoice?.invoiceType,
        invoice?.lineItems,
        invoice?.freightItems,
        invoice?.fuelItems,
        invoice?.restockingFeeItems,
        invoice?.ignoredParts,
        lineItemsUseDirectIndex,
        lineItemBackendIndexByCurrentIndex,
        localLineArithmeticIssues,
    ]);

    const splitItemTotalsWithDraft = useCallback(
        (items: Array<{ totalBuyPrice?: number; buyPrice?: number }>, keyPrefix: string) =>
            (items ?? []).reduce(
                (acc, item, idx) => {
                    const cellKey = `${keyPrefix}-${idx}-totalBuyPrice`;
                    const committed = typeof (item.totalBuyPrice ?? item.buyPrice) === 'number' ? (item.totalBuyPrice ?? item.buyPrice)! : 0;
                    const value = parseDraftNumeric(cellKey, committed);
                    if (value < 0) acc.negative += value;
                    else acc.positive += value;
                    return acc;
                },
                { positive: 0, negative: 0 }
            ),
        [parseDraftNumeric]
    );
    const lineItemsTotalsSplit = useMemo(
        () => splitItemTotalsWithDraft(editedFields.lineItems ?? invoice?.lineItems ?? [], 'lineItem'),
        [editedFields.lineItems, invoice?.lineItems, splitItemTotalsWithDraft]
    );
    const freightTotalsSplit = useMemo(
        () => splitItemTotalsWithDraft(editedFields.freightItems ?? invoice?.freightItems ?? [], 'freightItem'),
        [editedFields.freightItems, invoice?.freightItems, splitItemTotalsWithDraft]
    );
    const fuelTotalsSplit = useMemo(
        () => splitItemTotalsWithDraft(editedFields.fuelItems ?? invoice?.fuelItems ?? [], 'fuelItem'),
        [editedFields.fuelItems, invoice?.fuelItems, splitItemTotalsWithDraft]
    );
    const restockingTotalsSplit = useMemo(() => {
        const raw = editedFields.restockingFeeItems ?? invoice?.restockingFeeItems ?? [];
        const skipCc = (editedFields.paySettlementType ?? invoice?.paySettlementType) === 'charge';
        return raw.reduce(
            (acc, item, idx) => {
                if (skipCc && isCreditCardMiscFeeLine(item)) return acc;
                const cellKey = `restockingFeeItem-${idx}-totalBuyPrice`;
                const committed =
                    typeof (item.totalBuyPrice ?? (item as { buyPrice?: number }).buyPrice) === 'number'
                        ? (item.totalBuyPrice ?? (item as { buyPrice?: number }).buyPrice)!
                        : 0;
                const value = parseDraftNumeric(cellKey, committed);
                if (value < 0) acc.negative += value;
                else acc.positive += value;
                return acc;
            },
            { positive: 0, negative: 0 }
        );
    }, [
        editedFields.restockingFeeItems,
        invoice?.restockingFeeItems,
        editedFields.paySettlementType,
        invoice?.paySettlementType,
        parseDraftNumeric,
    ]);
    const lineItemsTotal = lineItemsTotalsSplit.positive + lineItemsTotalsSplit.negative;
    const freightTotal = freightTotalsSplit.positive + freightTotalsSplit.negative;
    const fuelItemsTotal = fuelTotalsSplit.positive + fuelTotalsSplit.negative;
    const restockingFeeTotal = restockingTotalsSplit.positive + restockingTotalsSplit.negative;
    const discountTotal = useMemo(() => {
        const dt = editedFields.discountTotal ?? invoice?.discountTotal;
        return typeof dt === 'number' ? dt : 0;
    }, [editedFields.discountTotal, invoice?.discountTotal]);

    /** Line Items + Freight + Fuel + Misc tabs only. Ignored tab is excluded so moved rows do not net against active tabs. */
    const aboveLineItemsSubtotal = lineItemsTotal + freightTotal + fuelItemsTotal + restockingFeeTotal;

    const salesTaxLineAmountInAboveLine = useMemo(() => {
        const items = editedFields.lineItems ?? invoice?.lineItems ?? [];
        return items.reduce((s, p) => {
            if (!isSalesTaxLineItemPart(p)) return s;
            const v = p.totalBuyPrice ?? p.unitBuyPrice;
            return s + (typeof v === 'number' && Number.isFinite(v) ? v : 0);
        }, 0);
    }, [editedFields.lineItems, invoice?.lineItems]);

    /** Above-line subtotal excluding Sales Tax rows — base for location default tax when setting is on. */
    const aboveLineItemsSubtotalExcludingSalesTax = aboveLineItemsSubtotal - salesTaxLineAmountInAboveLine;

    const isReadOnlyInvoice =
        invoice?.status === 'submitted' ||
        invoice?.status === 'manually_submitted';

    useEffect(() => {
        if (!invoice || !id || isReadOnlyInvoice || !salesTaxAsLineItem) return;
        const nexTaxFormat = (editedFields.nexTaxFormat ?? invoice.nexTaxFormat) || 'single';
        if (nexTaxFormat === 'split') return;
        const lineItems = editedFields.lineItems ?? invoice.lineItems ?? [];
        const taxesTotal =
            typeof editedFields.taxesTotal === 'number'
                ? editedFields.taxesTotal
                : typeof invoice.taxesTotal === 'number'
                  ? invoice.taxesTotal
                  : 0;
        const result = materializeSalesTaxAsLineItem({
            lineItems,
            taxesTotal,
            salesTaxAsLineItem: true,
            nexTaxFormat,
        });
        if (!result.materialized) return;
        setEditedFields((prev) => ({
            ...prev,
            lineItems: result.lineItems,
            taxesTotal: 0,
        }));
        // Intentionally omit editedFields from deps — only rematerialize when invoice/setting changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice, id, salesTaxAsLineItem, isReadOnlyInvoice]);

    const grandTotalTaxInputs = useCallback(() => {
        const freightRows = editedFields.freightItems ?? invoice?.freightItems ?? [];
        const format = (editedFields.nexTaxFormat ?? invoice?.nexTaxFormat) || 'single';
        const pst = format === 'split' ? (editedFields.pstTaxTotal ?? invoice?.pstTaxTotal ?? 0) : 0;
        return {
            isCanadian: isCanadianShop,
            pstTaxTotal: typeof pst === 'number' ? pst : 0,
            discountTotal: getSummaryNumberWhileEditing('discountTotal'),
            headerFreightForTotal: freightRows.length > 0 ? 0 : getSummaryNumberWhileEditing('freightTotal'),
            extractedTotal: getSummaryNumberWhileEditing('orderTotal'),
        };
    }, [
        editedFields.freightItems,
        editedFields.nexTaxFormat,
        editedFields.pstTaxTotal,
        invoice?.freightItems,
        invoice?.nexTaxFormat,
        invoice?.pstTaxTotal,
        isCanadianShop,
        getSummaryNumberWhileEditing,
    ]);

    const defaultTaxForSubtotal = useCallback(
        (subtotal: number): number => {
            if (locationDefaultTaxRate == null) return 0;
            return locationDefaultTaxForGrandTotal({
                taxRate: locationDefaultTaxRate,
                aboveLineItemsSubtotal: subtotal,
                ...grandTotalTaxInputs(),
            });
        },
        [locationDefaultTaxRate, grandTotalTaxInputs],
    );

    const getTaxesTotalForArithmetic = useCallback((): number => {
        const nexTaxFormat = (editedFields.nexTaxFormat ?? invoice?.nexTaxFormat) || 'single';
        if (salesTaxAsLineItem && nexTaxFormat !== 'split') {
            // Tax amount lives on the Sales Tax line item (included in above-line subtotal).
            return 0;
        }
        if (locationDefaultTaxRate != null && !taxManualOverride && !isReadOnlyInvoice) {
            if (editingNumericField === 'summary-taxesTotal') {
                return getSummaryNumberWhileEditing('taxesTotal');
            }
            return defaultTaxForSubtotal(aboveLineItemsSubtotal);
        }
        return getSummaryNumberWhileEditing('taxesTotal');
    }, [
        salesTaxAsLineItem,
        editedFields.nexTaxFormat,
        invoice?.nexTaxFormat,
        locationDefaultTaxRate,
        taxManualOverride,
        isReadOnlyInvoice,
        editingNumericField,
        aboveLineItemsSubtotal,
        defaultTaxForSubtotal,
        getSummaryNumberWhileEditing,
    ]);

    const getSummaryNumberValue = useCallback(
        (field: keyof InvoiceDetailResponse): number => {
            if (field === 'taxesTotal') return getTaxesTotalForArithmetic();
            return getNumberValue(field);
        },
        [getTaxesTotalForArithmetic, getNumberValue],
    );

    const commitTaxesTotal = useCallback(
        (parsed: number) => {
            taxUserEditedRef.current = true;
            const computed =
                locationDefaultTaxRate != null ? defaultTaxForSubtotal(aboveLineItemsSubtotal) : null;
            const isManual =
                computed == null || Math.abs(parsed - computed) > LOCATION_DEFAULT_TAX_COMPARE_TOLERANCE;
            setTaxManualOverride(isManual);
            updateField('taxesTotal', parsed);
        },
        [locationDefaultTaxRate, aboveLineItemsSubtotal, defaultTaxForSubtotal, updateField],
    );

    useEffect(() => {
        if (isReadOnlyInvoice || taxManualOverride || locationDefaultTaxRate == null) return;
        if (editingNumericField === 'summary-taxesTotal') return;
        const nexTaxFormat = (editedFields.nexTaxFormat ?? invoice?.nexTaxFormat) || 'single';
        if (salesTaxAsLineItem && nexTaxFormat !== 'split') {
            const nextTax = defaultTaxForSubtotal(aboveLineItemsSubtotalExcludingSalesTax);
            setEditedFields((prev) => {
                const currentLines = prev.lineItems ?? invoice?.lineItems ?? [];
                const taxIdx = currentLines.findIndex((p) => isSalesTaxLineItemPart(p));
                if (taxIdx < 0) {
                    if (Math.abs(nextTax) < 0.005) return prev;
                    return {
                        ...prev,
                        lineItems: [
                            ...currentLines,
                            {
                                title: 'Sales Tax',
                                partNumber: 'sales-tax',
                                qty: 1,
                                listPrice: 0,
                                unitBuyPrice: nextTax,
                                totalBuyPrice: nextTax,
                                confidence: 1,
                                salesTaxLineItem: true,
                            },
                        ],
                        taxesTotal: 0,
                    };
                }
                const existing = currentLines[taxIdx];
                const prior =
                    typeof existing.totalBuyPrice === 'number' ? existing.totalBuyPrice : existing.unitBuyPrice ?? 0;
                if (Math.abs(prior - nextTax) <= LOCATION_DEFAULT_TAX_COMPARE_TOLERANCE) {
                    return prev.taxesTotal === 0 || prev.taxesTotal === undefined
                        ? prev
                        : { ...prev, taxesTotal: 0 };
                }
                const nextLines = [...currentLines];
                nextLines[taxIdx] = {
                    ...existing,
                    title: 'Sales Tax',
                    qty: 1,
                    listPrice: 0,
                    unitBuyPrice: nextTax,
                    totalBuyPrice: nextTax,
                    salesTaxLineItem: true,
                };
                return { ...prev, lineItems: nextLines, taxesTotal: 0 };
            });
            return;
        }
        const nextTax = defaultTaxForSubtotal(aboveLineItemsSubtotal);
        setEditedFields((prev) => (prev.taxesTotal === nextTax ? prev : { ...prev, taxesTotal: nextTax }));
    }, [
        aboveLineItemsSubtotal,
        aboveLineItemsSubtotalExcludingSalesTax,
        locationDefaultTaxRate,
        taxManualOverride,
        isReadOnlyInvoice,
        editingNumericField,
        salesTaxAsLineItem,
        editedFields.nexTaxFormat,
        invoice?.nexTaxFormat,
        invoice?.lineItems,
        defaultTaxForSubtotal,
    ]);

    const renderNumericField = useMemo(
        () =>
            createRenderSummaryNumericField({
                isCurrentReturnInvoice,
                editingNumericField,
                editingNumericValues,
                setEditingNumericField,
                setEditingNumericValues,
                getNumberValue: getSummaryNumberValue,
                updateField,
                commitTaxesTotal,
                locationDefaultTaxRate,
                onTeachableAmountCommitted,
                summaryEditBaselineRef,
            }),
        [
            isCurrentReturnInvoice,
            editingNumericField,
            editingNumericValues,
            getSummaryNumberValue,
            updateField,
            commitTaxesTotal,
            locationDefaultTaxRate,
            onTeachableAmountCommitted,
        ],
    );

    const currentTaxesTotal = getTaxesTotalForArithmetic();
    const currentFreightTotal = getSummaryNumberWhileEditing('freightTotal');
    const currentOrderTotal = getSummaryNumberWhileEditing('orderTotal');
    const currentDiscountTotal = getSummaryNumberWhileEditing('discountTotal');
    const freightLineItemRows = editedFields.freightItems ?? invoice?.freightItems ?? [];
    const headerFreightForTotal = freightLineItemRows.length > 0 ? 0 : currentFreightTotal;

    const submitPayloadPreview = useMemo<InvoiceSubmitPayloadPreview | null>(() => {
        if (!invoice) return null;
        const previewArgs = buildNexSubmitPreviewArgs();
        if (!previewArgs) return null;

        const toTwoDecimals = (value: number): number => Math.round((value || 0) * 100) / 100;

        const manualOverrides: Record<number, number | undefined> = { ...manualPoHeaderSelections };

        const built = computeNexSubmitApDistPreview({
            ...previewArgs.base,
            manualPoHeaderOverrides: manualOverrides,
            allowNullPoHeaderForUnmatchedBillable: previewArgs.allowNullPoHeaderForUnmatchedBillable,
            unmatchedBillablePartIndices: previewArgs.unmatchedBillablePartIndices,
            salesTaxAsLineItem,
            taxesTotal: getTaxesTotalForArithmetic(),
            nexTaxFormat: (editedFields.nexTaxFormat ?? invoice.nexTaxFormat) || 'single',
        });
        if (!built) return null;

        const { apDist } = built;
        const currentTaxesTotal = getTaxesTotalForArithmetic();
        const currentNexTaxFormat = (editedFields.nexTaxFormat ?? invoice.nexTaxFormat) || 'single';
        const emitSalesTaxAsLine = shouldEmitSalesTaxAsApDistLine({
            salesTaxAsLineItem,
            nexTaxFormat: currentNexTaxFormat,
            taxesTotal: currentTaxesTotal,
        });
        const invoiceAmount = toTwoDecimals(
            aboveLineItemsSubtotal + (emitSalesTaxAsLine ? currentTaxesTotal : 0),
        );
        const currentPstTaxTotal = editedFields.pstTaxTotal ?? invoice.pstTaxTotal ?? 0;
        const taxFields = resolveNexApHeaderTaxFields({
            nexTaxFormat: currentNexTaxFormat,
            taxesTotal: currentTaxesTotal,
            pstTaxTotal: currentPstTaxTotal,
            salesTaxAsLineItem,
        });
        const discount = toTwoDecimals(getSummaryNumberWhileEditing('discountTotal'));
        const headerFreight = toTwoDecimals(headerFreightForTotal);
        const total = toTwoDecimals(getSummaryNumberWhileEditing('orderTotal'));
        const invoiceType = previewArgs.base.invoiceType === 'returned' ? 'CM' : 'REG';
        const invoiceDate = getDateValue().toISOString().slice(0, 10);
        const invoiceNo = String(editedFields.invoiceNumber ?? invoice.invoiceNumber ?? '');

        return {
            apHeader: {
                invoiceNo,
                invoiceDate,
                invoiceAmount,
                freight: headerFreight,
                discount,
                ...taxFields,
                total,
                invoiceType,
            },
            apDist,
            submitBlocked: built.submitBlocked,
            manualPoLineInfos: built.manualPoLineInfos,
            unmatchedManualLineCount: built.unmatchedManualLineCount,
        };
    }, [
        invoice,
        editedFields,
        buildNexSubmitPreviewArgs,
        getDateValue,
        manualPoHeaderSelections,
        aboveLineItemsSubtotal,
        headerFreightForTotal,
        getSummaryNumberWhileEditing,
        getTaxesTotalForArithmetic,
        salesTaxAsLineItem,
    ]);

    const submitManualPoLineInfos: ManualPoLineInfo[] = submitPayloadPreview?.manualPoLineInfos ?? [];

    const currentPstForTotal = (editedFields.nexTaxFormat ?? invoice?.nexTaxFormat) === 'split'
        ? (editedFields.pstTaxTotal ?? invoice?.pstTaxTotal ?? 0)
        : 0;
    /** Expected order total: above-line-items subtotal + tax (GST/HST) + PST + discount + conditional header freight. */
    const expectedOrderTotalFromLines =
        aboveLineItemsSubtotal + currentTaxesTotal + currentPstForTotal + currentDiscountTotal + headerFreightForTotal;

    const { localTotalFormulaMismatch, localGrandTotalVsAboveLineItemsWithFeesMismatch } = useMemo(
        () => computeLocalOrderTotalMismatchFlags(currentOrderTotal, expectedOrderTotalFromLines),
        [currentOrderTotal, expectedOrderTotalFromLines],
    );

    const buildTabData = (label: string, count: number, total: number, showTotal = true) => {
        let badge = `${count}`;
        if (count > 0 && showTotal && Math.abs(total) >= 0.01) {
            const sign = total < 0 ? '-' : '';
            badge = `${count}, ${sign}$${Math.abs(total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        return { label, badge, count };
    };

    const fromInvoiceMatched = invoice?.reconciliationIssues?.matchedNexLines ?? [];
    const effectiveMatchedNexLines = fromInvoiceMatched.length > 0 ? fromInvoiceMatched : preservedReconciliationRef.current.matchedNexLines;
    const fromInvoiceNotes = (invoice?.reconciliationIssues as { reconciliationMatchNotes?: Array<{ partIndex: number; invoicePartNumber: string; nexPartNumber: string; reason: string }> } | undefined)?.reconciliationMatchNotes ?? [];
    const effectiveReconciliationMatchNotes = fromInvoiceNotes.length > 0 ? fromInvoiceNotes : preservedReconciliationRef.current.reconciliationMatchNotes;

    const isReadOnly =
        invoice?.status === 'submitted' ||
        invoice?.status === 'manually_submitted';
    const renderLineItemNumericCell = (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) =>
        renderLineItemNumericCellFn({
            index,
            field,
            fw,
            editingNumericField,
            editingNumericValues,
            setEditingNumericField,
            setEditingNumericValues,
            getLineItemValue,
            getLineItemPriceValue,
            updateLineItem,
            isLineItemFieldEdited,
            openRevertModal,
            isReadOnly,
            lineItemGridPriceFocusHandoff: true,
        });
    const renderFreightItemNumericCell = (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) =>
        renderFreightItemNumericCellFn({
            index,
            field,
            fw,
            editingNumericField,
            editingNumericValues,
            setEditingNumericField,
            setEditingNumericValues,
            getFreightItemValue,
            getFreightItemPriceValue,
            updateFreightItem,
            isReadOnly,
            pasteGridPriceHandoff: !isReadOnly,
        });
    const renderFuelItemNumericCell = (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) =>
        renderFuelItemNumericCellFn({ index, field, fw, editingNumericField, editingNumericValues, setEditingNumericField, setEditingNumericValues, getFuelItemValue, getFuelItemPriceValue, updateFuelItem });
    const renderRestockingFeeItemNumericCell = (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) =>
        renderRestockingFeeItemNumericCellFn({
            index,
            field,
            fw,
            editingNumericField,
            editingNumericValues,
            setEditingNumericField,
            setEditingNumericValues,
            getRestockingFeeItemValue,
            getRestockingFeeItemPriceValue,
            updateRestockingFeeItem,
            isReadOnly,
            pasteGridPriceHandoff: !isReadOnly,
        });
    const renderIgnoredPartNumericCell = (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) =>
        renderIgnoredPartNumericCellFn({
            index,
            field,
            fw,
            editingNumericField,
            editingNumericValues,
            setEditingNumericField,
            setEditingNumericValues,
            getIgnoredPartValue,
            getIgnoredPartPriceValue,
            updateIgnoredPart,
            isReadOnly,
            pasteGridPriceHandoff: !isReadOnly,
        });

    return {
        editedFields, setEditedFields, isEditingInvoiceNumber, setIsEditingInvoiceNumber, invoiceInputRef,
        editingNumericField, editingNumericValues, setEditingNumericField, setEditingNumericValues,
        revertModal, resetConfirmOpen, setResetConfirmOpen, submitConfirmOpen, setSubmitConfirmOpen,
        suggestionModal, setSuggestionModal, vendorSuggestionModal, setVendorSuggestionModal,
        currentImagePage, setCurrentImagePage, activeRhsTab, setActiveRhsTab, lineItemsTabIndex, setLineItemsTabIndex,
        zoomImageRef, updateInvoiceMutation, invoiceUpdateInProgress, savedFieldsRef, isMutatingRef, isAutoSaveRef,
        updateField, updateLineItem, restoreLineItemRowsSnapshot, addLineItem, addLineItemAbove, addLineItemAt, duplicateLineItemAt, removeLineItem, reorderLineItems,
        reorderFreightItems, reorderRestockingFeeItems, reorderIgnoredParts,
        addFreightItem, addFreightItemAt, addFreightItemAbove, duplicateFreightItemAt, removeFreightItem, updateFreightItem,
        addFuelItem, addFuelItemAt, removeFuelItem, updateFuelItem,
        addRestockingFeeItem, addRestockingFeeItemAt, addRestockingFeeItemAbove, duplicateRestockingFeeItemAt, removeRestockingFeeItem, updateRestockingFeeItem, restoreIgnoredPart,
        getRestockingFeeItemStringValue, getRestockingFeeItemPriceValue,
        getFreightItemStringValue, getFreightItemPriceValue,
        getFuelItemStringValue, getFuelItemPriceValue,
        getStringValue, getDateValue, getNumberValue: getSummaryNumberValue, getLineItemStringValue, getLineItemPriceValue,
        getOriginalValue, getOriginalLineItemValue, isFieldEdited, isLineItemFieldEdited, getLineItemEditedFieldLabels, hasAnyLineItemFieldEdited,
        openRevertModal, closeRevertModal, handleRevertFromModal, getRevertModalOriginalValue, getRevertModalCurrentValue,
        renderNumericField, hasChanges, canReset, getResetPayload,
        handleResetClick, handleSubmitClick, handleSubmitConfirm, handleResetConfirm, confirmInvoiceTypeChange,
        confirmShopLocationChange, confirmVendorChange, handleSave,
        buildCurrentReconciliationIssuesForSave: buildReconciliationIssuesForSave,
        getCurrentRoNumber, getOriginalRoNumber, hasRoNumberChanged,
        lineItemBackendIndexByCurrentIndex, remapSubmitPreviewPartIndex,
        lineItemsUseDirectIndex, lineItemsTotal, freightTotal, fuelItemsTotal, restockingFeeTotal, discountTotal, aboveLineItemsSubtotal, flaggedPartIndices, buildTabData,
        addIgnoredPart, addIgnoredPartAt, addIgnoredPartAbove, removeIgnoredPart, updateIgnoredPart, getIgnoredPartStringValue, getIgnoredPartPriceValue,
        renderLineItemNumericCell, renderFreightItemNumericCell, renderFuelItemNumericCell, renderRestockingFeeItemNumericCell,         renderIgnoredPartNumericCell, fmtUsd,
        effectiveMatchedNexLines, effectiveReconciliationMatchNotes,
        localTotalFormulaMismatch,
        localGrandTotalVsAboveLineItemsWithFeesMismatch,
        localLineArithmeticIssues,
        submitPayloadPreview,
        submitManualPoLineInfos,
        manualPoHeaderSelections,
        setManualPoHeaderSelections,
        isCurrentReturnInvoice,
        nexTaxFormat,
        pstTaxTotal: editedFields.pstTaxTotal ?? invoice?.pstTaxTotal ?? 0,
        setNexTaxFormat: (format: 'single' | 'split') => updateField('nexTaxFormat', format),
        setPstTaxTotal: (value: number) => updateField('pstTaxTotal', value),
        showCanadianTaxControls,
        handleNexTaxFormatChange: (format: 'single' | 'split') => {
            updateField('nexTaxFormat', format);
            if (format === 'split' && editedFields.pstTaxTotal == null && invoice?.pstTaxTotal == null) {
                updateField('pstTaxTotal', 0);
            }
        },
    };
}

export { fmtUsd, fmtUnitUsd, fmtDollar } from './detailForm/invoiceDetailFormFormatters';
export type { RhsTab, LineItemField, ReconciliationStateForForm, UseInvoiceDetailFormStateOptions } from './detailForm/invoiceDetailFormTypes';
